import { visitDayWords, visitWindowWords } from './jobMessages';

/**
 * 🔒 The notices a visit's own workers get (piece 29; T1245): a visit
 * dispatched to them, moved, or cancelled, and since decision 2250 a worker
 * taken off it. Each is an activity feed entry for every worker, and a text to
 * each with a mobile on file. These are the words and the quiet-window rules,
 * which need no database.
 */
export const VISIT_NOTICE_KINDS = ['Dispatched', 'Moved', 'Cancelled', 'Removed'] as const;
export type VisitNoticeKind = (typeof VISIT_NOTICE_KINDS)[number];

/** The feed entry's type for each kind, as the While You Were Gone tile groups it. */
export const VISIT_NOTICE_ENTRY_TYPES: Readonly<Record<VisitNoticeKind, string>> = {
  Dispatched: 'workOrderVisit.dispatched',
  Moved: 'workOrderVisit.moved',
  Cancelled: 'workOrderVisit.cancelled',
  Removed: 'workOrderVisit.removed',
};

export interface VisitNoticeFacts {
  workOrderNumber: string;
  visitLabel: string;
  companyName: string;
  siteName: string;
  windowStart: string;
  windowEnd: string | null;
  zone: string;
  /** Cancel's reason, as the desk typed it. */
  reason: string | null;
}

/**
 * 🔒 One notice's words: the feed's sentence and the text, each naming the job,
 * the visit, the site (whose label names the customer) and, where the visit still happens, its day and
 * window in the Organization's zone.
 */
export function visitNoticeWords(kind: VisitNoticeKind, facts: VisitNoticeFacts): { summary: string; text: string } {
  const what = `${facts.workOrderNumber} ${facts.visitLabel} at ${facts.siteName || facts.companyName}`;
  const when = `${visitDayWords(facts.windowStart, facts.zone)}, ${visitWindowWords(facts.windowStart, facts.windowEnd, facts.zone)}`;
  if (kind === 'Removed') {
    const summary = `You are no longer on ${what}.`;
    return { summary, text: `You are no longer on job ${what}.` };
  }
  const summary = kind === 'Dispatched'
    ? `${what} is dispatched to you for ${when}.`
    : kind === 'Moved'
      ? `${what} has moved to ${when}.`
      : `${what} is canceled${facts.reason ? `: ${facts.reason}` : ''}.`;
  return { summary, text: `Job ${summary}` };
}

/**
 * 🔒 The revision a notice's text is keyed on, so a retried notice is one text:
 * a move by the window it names, and a worker taken off by the moment the
 * window settled, so a second removal later is a second text.
 */
export function visitNoticeRevision(kind: VisitNoticeKind, visitId: string, windowStart: string, settledAt: string | null = null): string {
  if (kind === 'Moved') return `${visitId}.notice.${kind}.${windowStart}`;
  if (kind === 'Removed') return `${visitId}.notice.${kind}.${settledAt ?? windowStart}`;
  return `${visitId}.notice.${kind}`;
}

/** 🔒 Decision 2250: the minutes of quiet a visit's dispatch or move waits before its workers are told, seeded at 10. */
export const SCHEDULE_NOTICE_QUIET_MINUTES_KEY = 'schedule.noticeQuietMinutes';
export const SCHEDULE_NOTICE_QUIET_MINUTES_DEFAULT = 10;
export const SCHEDULE_NOTICE_QUIET_MINUTES_MAX = 120;

/** The refusal Settings › Service › Schedule Notices shows: a whole number of minutes from 0 to 120. */
export function noticeQuietMinutesProblem(value: unknown): string | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= SCHEDULE_NOTICE_QUIET_MINUTES_MAX
    ? null
    : `the quiet window is a whole number of minutes from 0 to ${SCHEDULE_NOTICE_QUIET_MINUTES_MAX}`;
}

/** The setting as read: a stored whole number in range, else the seeded 10. */
export function noticeQuietMinutesOf(value: unknown): number {
  return noticeQuietMinutesProblem(value) === null ? (value as number) : SCHEDULE_NOTICE_QUIET_MINUTES_DEFAULT;
}

/** The kinds a quiet window holds back; a cancel is told at once. */
export type VisitNoticeWaitingKind = 'Dispatched' | 'Moved';

/** What a visit's workers knew when its waiting notice began (decision 2250): the window, the crew, the kind, and who last changed it. */
export interface VisitNoticeFrom {
  windowStart: string;
  windowEnd: string | null;
  employmentIds: string[];
  kind: VisitNoticeWaitingKind;
  actorPersonId: string;
  /** 🔒 Decision 2360: a move on the Schedule view in this run holds the customer's Booked until the window settles. */
  customerBooked?: true;
}

/** A visit's window and crew as they stand. */
export interface VisitNoticeState {
  windowStart: string;
  windowEnd: string | null;
  employmentIds: readonly string[];
}

/** Reads the stored snapshot back, or null where none waits or it does not parse. */
export function visitNoticeFromOf(value: unknown): VisitNoticeFrom | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (typeof row.windowStart !== 'string' || (row.windowEnd !== null && typeof row.windowEnd !== 'string') || !Array.isArray(row.employmentIds)) return null;
  if (row.kind !== 'Dispatched' && row.kind !== 'Moved') return null;
  if (typeof row.actorPersonId !== 'string') return null;
  return {
    windowStart: row.windowStart, windowEnd: row.windowEnd as string | null, employmentIds: row.employmentIds.filter((id): id is string => typeof id === 'string'), kind: row.kind, actorPersonId: row.actorPersonId,
    ...(row.customerBooked === true ? { customerBooked: true as const } : {}),
  };
}

/**
 * 🔒 A dispatch or a move restamps the wait (decision 2250): the notice is due
 * the quiet window after this change. What the workers knew is kept from the
 * first change of the run; a dispatch anywhere in the run makes it a dispatch
 * notice; the person named is whoever changed it last. 🔒 A change that holds
 * the customer's Booked marks the run, and the mark stays to its end
 * (decision 2360).
 */
export function stageVisitNotice(
  waiting: VisitNoticeFrom | null,
  change: { kind: VisitNoticeWaitingKind; before: VisitNoticeState; actorPersonId: string; customerBooked?: boolean },
  now: Date,
  quietMinutes: number,
): { dueAt: Date; from: VisitNoticeFrom } {
  const base = waiting ?? { windowStart: change.before.windowStart, windowEnd: change.before.windowEnd, employmentIds: [...change.before.employmentIds], kind: change.kind, actorPersonId: change.actorPersonId };
  return {
    dueAt: new Date(now.getTime() + quietMinutes * 60_000),
    from: {
      ...base, kind: base.kind === 'Dispatched' || change.kind === 'Dispatched' ? 'Dispatched' : 'Moved', actorPersonId: change.actorPersonId,
      ...(base.customerBooked || change.customerBooked ? { customerBooked: true as const } : {}),
    },
  };
}

/**
 * 🔒 Whether the settled run sends the customer's Booked (decision 2360): a run
 * a Schedule move marked, whose window ended somewhere other than where it
 * began. A crew change alone, or a window moved back, tells the customer
 * nothing.
 */
export function settledCustomerBooked(from: VisitNoticeFrom, now: Pick<VisitNoticeState, 'windowStart' | 'windowEnd'>): boolean {
  return from.customerBooked === true && (from.windowStart !== now.windowStart || from.windowEnd !== now.windowEnd);
}

/**
 * 🔒 Who is told what once the window settles (decision 2250). A dispatch in
 * the run tells every worker on the visit it is dispatched to them. Otherwise a
 * window that moved tells every worker it moved, and a worker newly on a
 * dispatched visit is told it is dispatched to them. A worker on it before and
 * not now is told they are off it. A visit back on the window and crew it began
 * with tells nobody.
 */
export function settledVisitNotices(from: VisitNoticeFrom, now: VisitNoticeState & { dispatched: boolean }): { kind: VisitNoticeKind; employmentIds: string[] }[] {
  const before = new Set(from.employmentIds);
  const current = [...new Set(now.employmentIds)];
  const removed = from.employmentIds.filter((id, index, all) => all.indexOf(id) === index && !current.includes(id));
  const added = current.filter((id) => !before.has(id));
  const kept = current.filter((id) => before.has(id));
  const windowMoved = from.windowStart !== now.windowStart || from.windowEnd !== now.windowEnd;
  const out: { kind: VisitNoticeKind; employmentIds: string[] }[] = [];
  const push = (kind: VisitNoticeKind, employmentIds: string[]) => {
    if (employmentIds.length > 0) out.push({ kind, employmentIds });
  };
  if (from.kind === 'Dispatched') push('Dispatched', current);
  else {
    if (windowMoved) push('Moved', now.dispatched ? kept : current);
    if (now.dispatched) push('Dispatched', added);
  }
  push('Removed', removed);
  return out;
}
